import styled from '@emotion/styled';
import React, { useId, useImperativeHandle, useRef } from 'react';
import { colors, semantic_colors, shapes, text_styles } from '../../foundation';
import { checker } from './styles';
import { IconCancel } from '../icons/generated';

export interface ColorChipProps {
  color?: string;
  displayedColor?: string;
  label?: string;
  selected?: boolean;
  disabled?: boolean;
  onClick: (color: string) => void;
}

interface ColorChipInstance {
  focus: () => void;
}
export const ColorChip = React.forwardRef<ColorChipInstance, ColorChipProps>(
  ({ color, displayedColor, label, selected, disabled, onClick }, ref) => {
    const button_ref = useRef<HTMLButtonElement>(null);
    const tooltip_id = useId();

    const handleClick = () => {
      if (!color) {
        return;
      }
      onClick(color);
    };

    useImperativeHandle(ref, () => ({
      focus: () => {
        button_ref.current?.focus();
      },
    }));

    return (
      <Container type='button' selected={Boolean(selected)} disabled={Boolean(disabled)} ref={button_ref}>
        <SelectedBox />
        <ChipBg colored={!!color}>
          <Chip
            displayed_color={displayedColor || color}
            aria-labelledby={tooltip_id}
            selected={selected}
            disabled={disabled}
            onClick={handleClick}
            data-testid='color-picker-preset-color-block'
          >
            {disabled && <IconCancel color='white' width={12} height={12} />}
          </Chip>
        </ChipBg>
        {label && (
          <Tooltip className='tooltip' id={tooltip_id} role='tooltip'>
            {label}
          </Tooltip>
        )}
      </Container>
    );
  },
);

const ChipBg = styled.div<{ colored: boolean }>`
  ${({ colored }) => (colored ? checker : `background-color: ${colors.white}`)}
  width: 100%;
  height: 100%;
  ${shapes.border_radius.xsmall};
`;

const Chip = styled.div<{ displayed_color?: string; selected?: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.1) inset;
  ${shapes.border_radius.xsmall};

  ${({ displayed_color, disabled }) =>
    disabled
      ? `
      cursor: not-allowed;
      border-color: ${semantic_colors.content.disabled};
      background-color: ${semantic_colors.content.disabled};
  `
      : `
      cursor: pointer;
      ${!displayed_color ? checker : `background: ${displayed_color}`};
  `};
`;

const SelectedBox = styled.div`
  position: absolute;
  left: -2px;
  top: -2px;
  width: 100%;
  height: 100%;
  padding: 1px;
  border: 1px solid transparent;
  border-radius: 6px;
  pointer-events: none;
  box-sizing: content-box;
  transition: border-color 0.1s linear;
`;

const Tooltip = styled.div`
  position: absolute;
  top: 22px;
  left: 8px;
  transform: translateX(-50%);
  z-index: 1;
  padding: 0px 4px;
  border-radius: 4px;
  color: ${semantic_colors.content.primary_inverse};
  background: rgba(54, 54, 68, 0.8);
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  ${text_styles.Caption_11_Medium};
`;

const Container = styled.button<{ selected: boolean; disabled: boolean }>`
  position: relative;
  width: 16px;
  height: 16px;
  padding: 0;
  border: 0;
  background-color: transparent;
  &:hover ${Tooltip} {
    ${({ disabled }) => !disabled && `opacity: 1`}
  }
  ${SelectedBox} {
    ${({ disabled, selected }) =>
      !disabled &&
      selected &&
      `
        border-color: ${semantic_colors.border.active};
      `}
  }
  &:hover ${SelectedBox} {
    ${({ disabled, selected }) =>
      !disabled &&
      `
        border-color: ${selected ? semantic_colors.border.active : semantic_colors.border.primary};
      `}
  }
`;
